var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { DottedLine, InitialStepWrapper, LineThrough, ProgressIndicatorBarBase, ProgressIndicatorContainer, ProgressLine, StepWrapper, TooltipArrow, TooltipContent } from './subcomponents';
import Step from './Step';
import { Body } from '../../typography';
import { Inset } from '../../spacing';
import useWindowSize from '../../customHooks/useWindowWidth';
const ProgressIndicator = (_a) => {
    var _b, _c, _d;
    var { startStep, endStep, milestoneStep, progressPercentage, tooltipText, width = 448, className, dataCy } = _a, props = __rest(_a, ["startStep", "endStep", "milestoneStep", "progressPercentage", "tooltipText", "width", "className", "dataCy"]);
    const stepsRefs = useRef([]);
    const [popperReference, setPopperReference] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const [arrowElement, setArrowElement] = useState(null);
    const [tooltipPlacement, setTooltipPlacement] = useState('top');
    const [stepsPlacement, setStepsPlacement] = useState({
        startStepPlacement: 'bottom',
        milestoneStepPlacement: 'bottom',
        endStepPlacement: 'bottom'
    });
    const { isLessThanTablet } = useWindowSize();
    const dottedLineAndLineThroughWidth = milestoneStep === null || milestoneStep === void 0 ? void 0 : milestoneStep.value;
    const offsetModifier = useMemo(() => ({
        name: 'offset',
        options: {
            offset: () => {
                return [10, 20];
            }
        }
    }), []);
    const { styles: popperStyles, attributes: popperAttributes, update } = usePopper(popperReference, popperElement, {
        placement: tooltipPlacement,
        modifiers: [
            {
                name: 'arrow',
                options: { element: arrowElement, padding: 30 }
            },
            offsetModifier,
            {
                name: 'preventOverflow',
                options: {
                    boundary: 'viewport',
                    padding: 10,
                    mainAxis: false
                }
            },
            {
                name: 'flip',
                options: {
                    fallbackPlacements: []
                }
            }
        ]
    });
    useEffect(() => {
        const checkStepOverlapAndAdjust = () => {
            let startStepPlacement = 'bottom';
            let endStepPlacement = 'bottom';
            const stepsRects = stepsRefs.current
                .map((ref) => ref === null || ref === void 0 ? void 0 : ref.getBoundingClientRect())
                .filter((rect) => !!rect);
            /*
             *  This checks the overlap of the milestoneStepRect with  either the startStepRect or endStepRect
             *  if there is overlap then milestoneStepRect will be placed opposite to default placement
             */
            if (stepsRects.length === 3) {
                const [startStepRect, milestoneStepRect, endStepRect] = stepsRects;
                const overlapWithStartStep = startStepRect.right > milestoneStepRect.left &&
                    milestoneStepRect.right > startStepRect.left;
                const overlapWithEndStep = endStepRect.right > milestoneStepRect.left &&
                    milestoneStepRect.right > endStepRect.left;
                if (overlapWithStartStep || overlapWithEndStep) {
                    endStepPlacement = 'top';
                    startStepPlacement = 'top';
                }
            }
            setStepsPlacement({
                milestoneStepPlacement: 'bottom',
                endStepPlacement,
                startStepPlacement
            });
            if (update) {
                update();
            }
        };
        checkStepOverlapAndAdjust();
    }, [stepsRefs, isLessThanTablet, update]);
    useEffect(() => {
        const checkTooltipOverlay = () => {
            if (popperReference && popperElement) {
                if (isLessThanTablet) {
                    setTooltipPlacement('right');
                }
                else {
                    setTooltipPlacement('top');
                }
            }
        };
        checkTooltipOverlay();
    }, [popperReference, popperElement, isLessThanTablet]);
    return (React.createElement(ProgressIndicatorContainer, Object.assign({}, props, { className: className, "data-cy": dataCy }),
        React.createElement(ProgressIndicatorBarBase, { "$width": width },
            tooltipText && (React.createElement(TooltipContent, Object.assign({ ref: setPopperElement, style: popperStyles.popper }, popperAttributes.popper),
                React.createElement(Inset, { top: "xsmall", bottom: "xsmall", left: "medium", right: "medium" },
                    React.createElement(Body, null, tooltipText)),
                React.createElement(TooltipArrow, { ref: setArrowElement, style: popperStyles.arrow }))),
            tooltipText && (React.createElement(DottedLine, { "$width": dottedLineAndLineThroughWidth, ref: (element) => {
                    setPopperReference(element);
                } })),
            React.createElement(LineThrough, { "$width": dottedLineAndLineThroughWidth }),
            React.createElement(ProgressLine, { width: progressPercentage }),
            React.createElement(InitialStepWrapper, null,
                React.createElement(Step, { dataCy: (_b = startStep.dataCy) !== null && _b !== void 0 ? _b : `${dataCy}.stepper-element`, stepData: startStep.stepData, placement: stepsPlacement.startStepPlacement, ref: (element) => {
                        stepsRefs.current[0] = element;
                    } })),
            React.createElement(StepWrapper, { "$value": milestoneStep.value, className: milestoneStep === null || milestoneStep === void 0 ? void 0 : milestoneStep.className },
                milestoneStep.stepIcon,
                React.createElement(Step, { dataCy: (_c = milestoneStep.dataCy) !== null && _c !== void 0 ? _c : `${dataCy}.stepper-element`, stepData: milestoneStep.stepData, placement: stepsPlacement.milestoneStepPlacement, ref: (element) => {
                        stepsRefs.current[1] = element;
                    } })),
            React.createElement(StepWrapper, { "$value": 100, className: endStep === null || endStep === void 0 ? void 0 : endStep.className },
                endStep.stepIcon,
                React.createElement(Step, { dataCy: (_d = endStep.dataCy) !== null && _d !== void 0 ? _d : `${dataCy}.stepper-element`, stepData: endStep.stepData, placement: stepsPlacement.endStepPlacement, ref: (element) => {
                        stepsRefs.current[2] = element;
                    } })))));
};
export default ProgressIndicator;
